import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CompareArrows, Gavel, Search as SearchIcon } from '@mui/icons-material';
import { bidComparisonApi, projectsApi } from '../../services/benchmarkApi';
import { formatMoney, projectLabel } from './benchmarkFormat';

const BRAND = '#8B1538';

/** Distinct accents for bidder chips/cards (cycles if more bidders). */
const BIDDER_PALETTE = [
  { bg: '#F3E5F0', border: '#8B1538', text: '#6B0F2A' },
  { bg: '#E3F2FD', border: '#1565C0', text: '#0D47A1' },
  { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20' },
  { bg: '#FFF3E0', border: '#EF6C00', text: '#E65100' },
  { bg: '#E0F7FA', border: '#00838F', text: '#006064' },
  { bg: '#FCE4EC', border: '#C2185B', text: '#880E4F' },
];

const moneyCell = (v) => (v == null || v === '' ? '—' : formatMoney(v));

const qtyCell = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
};

/**
 * Bid Comparison — all bids uploaded under one project (dataset_kind=BID).
 */
const BenchmarkBidComparison = () => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const reloadComparison = () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    bidComparisonApi
      .forProject(projectId)
      .then((res) => setData(res.data))
      .catch((e) => {
        setData(null);
        setError(e.message || 'Failed to load bid comparison');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    projectsApi
      .list()
      .then((res) => setProjects(res.data || []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      return;
    }
    setSearch('');
    reloadComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when project changes
  }, [projectId]);

  const bidderColors = useMemo(() => {
    const map = {};
    (data?.bidders || []).forEach((b, i) => {
      map[b] = BIDDER_PALETTE[i % BIDDER_PALETTE.length];
    });
    return map;
  }, [data?.bidders]);

  const columns = useMemo(() => {
    const bidders = data?.bidders || [];
    const cols = [
      {
        field: 'structureCode',
        headerName: 'Code',
        width: 88,
        headerClassName: 'bc-col-meta',
        cellClassName: 'bc-col-meta',
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        headerClassName: 'bc-col-meta',
        cellClassName: 'bc-col-meta',
      },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 96,
        type: 'number',
        headerClassName: 'bc-col-meta',
        cellClassName: 'bc-col-meta',
        valueFormatter: (v) => qtyCell(v),
      },
      {
        field: 'unit',
        headerName: 'Unit',
        width: 72,
        headerClassName: 'bc-col-meta',
        cellClassName: 'bc-col-meta',
      },
    ];
    bidders.forEach((b) => {
      const pal = bidderColors[b] || BIDDER_PALETTE[0];
      cols.push({
        field: `rate_${b}`,
        headerName: b,
        width: 118,
        type: 'number',
        headerClassName: 'bc-col-bidder',
        cellClassName: (params) => {
          const v = params.value;
          if (v == null) return 'bc-col-bidder';
          if (params.row.minRate != null && Number(v) === Number(params.row.minRate)) {
            return 'bc-col-bidder bc-cell-low';
          }
          if (params.row.maxRate != null && Number(v) === Number(params.row.maxRate)) {
            return 'bc-col-bidder bc-cell-high';
          }
          return 'bc-col-bidder';
        },
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: pal.border,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: pal.text, textTransform: 'none' }}>
              {b}
            </Typography>
          </Box>
        ),
        valueFormatter: (v) => moneyCell(v),
      });
    });
    cols.push(
      {
        field: 'averageRate',
        headerName: 'Average',
        width: 110,
        type: 'number',
        headerClassName: 'bc-col-stat',
        cellClassName: 'bc-col-stat',
        valueFormatter: (v) => moneyCell(v),
      },
      {
        field: 'medianRate',
        headerName: 'Median',
        width: 110,
        type: 'number',
        headerClassName: 'bc-col-stat bc-col-median',
        cellClassName: 'bc-col-stat bc-col-median',
        valueFormatter: (v) => moneyCell(v),
      },
      {
        field: 'minRate',
        headerName: 'Low',
        width: 100,
        type: 'number',
        headerClassName: 'bc-col-stat bc-col-low',
        cellClassName: 'bc-col-stat bc-col-low',
        valueFormatter: (v) => moneyCell(v),
      },
      {
        field: 'maxRate',
        headerName: 'High',
        width: 100,
        type: 'number',
        headerClassName: 'bc-col-stat bc-col-high',
        cellClassName: 'bc-col-stat bc-col-high',
        valueFormatter: (v) => moneyCell(v),
      }
    );
    return cols;
  }, [data, bidderColors]);

  const allRows = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.map((r, i) => {
      const row = {
        id: i,
        structureCode: r.structureCode,
        description: r.description,
        quantity: r.quantity,
        unit: r.unit,
        averageRate: r.averageRate,
        medianRate: r.medianRate,
        minRate: r.minRate,
        maxRate: r.maxRate,
      };
      (data.bidders || []).forEach((b) => {
        row[`rate_${b}`] = r.ratesByBidder?.[b] ?? null;
      });
      return row;
    });
  }, [data]);

  const rows = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => {
      const hay = [
        r.structureCode,
        r.description,
        r.unit,
        r.quantity != null ? String(r.quantity) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allRows, search]);

  const summaries = data?.bidderSummaries || [];
  const lowestTotal =
    summaries.length > 0
      ? Math.min(...summaries.map((s) => Number(s.totalAmount) || 0))
      : null;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 1.5,
        background: 'linear-gradient(165deg, #FAF7F8 0%, #F5F5F7 45%, #EEF4F8 100%)',
      }}
    >
      {/* Fixed header block — does not scroll */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 1.25,
            pb: 1,
            borderBottom: `3px solid ${BRAND}`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.25,
              bgcolor: BRAND,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 3px 10px ${BRAND}40`,
              flexShrink: 0,
            }}
          >
            <Gavel sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 750, color: '#2A1520', lineHeight: 1.2 }}>
              Bid Comparison
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Line rates with quantity, average, median, low &amp; high
            </Typography>
          </Box>
          {data?.bidders?.length > 0 && (
            <Chip
              icon={<CompareArrows sx={{ fontSize: '0.95rem !important' }} />}
              label={`${data.bidders.length} bidder${data.bidders.length === 1 ? '' : 's'}`}
              size="small"
              sx={{
                bgcolor: `${BRAND}14`,
                color: BRAND,
                fontWeight: 600,
                border: `1px solid ${BRAND}33`,
              }}
            />
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1.25,
            mb: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 280, maxWidth: 400 }}>
            <InputLabel>Project</InputLabel>
            <Select
              label="Project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: `${BRAND}55` },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND },
              }}
            >
              {projects.map((p) => (
                <MenuItem key={p.id || p.projectId} value={p.id || p.projectId}>
                  {projectLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {data && summaries.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'stretch', flex: 1 }}>
              {summaries.map((s) => {
                const pal = bidderColors[s.bidderName] || BIDDER_PALETTE[0];
                const isLowest =
                  lowestTotal != null && Number(s.totalAmount) === Number(lowestTotal);
                return (
                  <Box
                    key={s.bidderName}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.25,
                      bgcolor: pal.bg,
                      border: `1px solid ${pal.border}`,
                      borderLeft: `3px solid ${pal.border}`,
                      minWidth: 118,
                      position: 'relative',
                    }}
                  >
                    {isLowest && (
                      <Chip
                        label="Lowest"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          height: 15,
                          fontSize: '0.58rem',
                          fontWeight: 700,
                          bgcolor: '#2E7D32',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.6 },
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: pal.text,
                        textTransform: 'uppercase',
                        fontSize: '0.62rem',
                        letterSpacing: 0.3,
                        pr: isLowest ? 4.5 : 0,
                      }}
                    >
                      {s.bidderName}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}>
                      {formatMoney(s.totalAmount)}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {s.lineCount} line{s.lineCount === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 1.25 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Grid fills remaining viewport — scroll inside DataGrid only */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        )}

        {!loading && !projectId && (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: `${BRAND}44`,
              bgcolor: '#fff',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Select a project to load Bid uploads and comparison stats.
            </Typography>
          </Paper>
        )}

        {!loading && data && summaries.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#FFF8E1',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No Bid uploads on this project yet. Use Upload Estimate → Bid upload and enter a
              bidder name.
            </Typography>
          </Paper>
        )}

        {!loading && data && summaries.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(42,21,32,0.07)',
              '& .MuiDataGrid-root': { border: 'none', height: '100%' },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#F8F0F3',
                borderBottom: `2px solid ${BRAND}33`,
              },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
              '& .bc-col-stat': { bgcolor: 'rgba(139, 21, 56, 0.04)' },
              '& .bc-col-median': { bgcolor: 'rgba(21, 101, 192, 0.06) !important' },
              '& .bc-col-low .MuiDataGrid-columnHeaderTitle, & .bc-cell-low': {
                color: '#1B5E20',
              },
              '& .bc-col-low': { bgcolor: 'rgba(46, 125, 50, 0.08) !important' },
              '& .bc-col-high': { bgcolor: 'rgba(198, 40, 40, 0.06) !important' },
              '& .bc-col-high .MuiDataGrid-columnHeaderTitle, & .bc-cell-high': {
                color: '#B71C1C',
              },
              '& .bc-cell-low': {
                bgcolor: 'rgba(46, 125, 50, 0.12) !important',
                fontWeight: 700,
              },
              '& .bc-cell-high': {
                bgcolor: 'rgba(198, 40, 40, 0.1) !important',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(139, 21, 56, 0.03)' },
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: '#FAFAFA',
                flexShrink: 0,
              }}
            >
              <TextField
                size="small"
                placeholder="Search code, description, unit, qty…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  minWidth: 320,
                  width: { xs: '100%', sm: 340 },
                  maxWidth: 420,
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND },
                  },
                  '& .MuiInputBase-input': {
                    overflow: 'visible',
                    textOverflow: 'clip',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {rows.length === allRows.length
                  ? `${allRows.length} lines`
                  : `${rows.length} of ${allRows.length} lines`}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Legend:
              </Typography>
              <Chip size="small" label="Lowest in row" sx={{ height: 22, bgcolor: 'rgba(46,125,50,0.15)', color: '#1B5E20', fontWeight: 600 }} />
              <Chip size="small" label="Highest in row" sx={{ height: 22, bgcolor: 'rgba(198,40,40,0.12)', color: '#B71C1C', fontWeight: 600 }} />
              <Chip size="small" label="Median / stats" sx={{ height: 22, bgcolor: 'rgba(21,101,192,0.1)', color: '#0D47A1', fontWeight: 600 }} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={{
                  height: '100%',
                  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                  },
                }}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default BenchmarkBidComparison;
